import { useState } from 'react';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';

import { Colors } from '@/constants/theme';
import { BorderRadius, Spacing } from '@/constants/spacing';
import { getBodyFont } from '@/constants/typography';
import { useColorScheme } from '@/hooks/use-color-scheme';
import type { RoutePlanStop } from '@/types/route-plan';

type UnmappedStopsChipProps = {
  stops: RoutePlanStop[];
};

/**
 * "n stops not on map" chip (bottom-left of the full-screen map). Tapping
 * toggles a list of the stops whose location text failed to geocode, with
 * a hint to edit the location. Locations are flagged, never guessed.
 */
export function UnmappedStopsChip({ stops }: UnmappedStopsChipProps) {
  const colorScheme = useColorScheme();
  const palette = Colors[colorScheme ?? 'light'];
  const [expanded, setExpanded] = useState(false);

  if (stops.length === 0) return null;
  const label = stops.length === 1 ? '1 stop not on map' : `${stops.length} stops not on map`;

  return (
    <View style={styles.wrap} pointerEvents="box-none">
      {expanded && (
        <View style={[styles.panel, { backgroundColor: palette.cardBackground }]}>
          {stops.map((stop) => (
            <Text
              key={stop.id}
              style={[styles.panelRow, { color: palette.text, fontFamily: getBodyFont() }]}
              numberOfLines={2}
            >
              {stop.title} — couldn&apos;t find &quot;{stop.location}&quot; on the map. Edit the
              location to fix.
            </Text>
          ))}
        </View>
      )}
      <TouchableOpacity
        style={[styles.chip, { backgroundColor: palette.cardBackground }]}
        onPress={() => setExpanded((current) => !current)}
        activeOpacity={0.7}
      >
        <Text style={[styles.chipLabel, { color: palette.textMuted, fontFamily: getBodyFont() }]}>
          {label}
        </Text>
      </TouchableOpacity>
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: {
    position: 'absolute',
    left: Spacing.md,
    bottom: Spacing.md,
    gap: Spacing.xs,
    maxWidth: 280,
  },
  chip: {
    alignSelf: 'flex-start',
    paddingVertical: Spacing.xxs,
    paddingHorizontal: Spacing.sm,
    borderRadius: BorderRadius.lg,
    opacity: 0.94,
    shadowColor: '#000',
    shadowOpacity: 0.12,
    shadowRadius: 4,
    shadowOffset: { width: 0, height: 2 },
    elevation: 2,
  },
  chipLabel: {
    fontSize: 12,
  },
  panel: {
    borderRadius: BorderRadius.md,
    padding: Spacing.sm,
    gap: Spacing.xs,
    shadowColor: '#000',
    shadowOpacity: 0.12,
    shadowRadius: 6,
    shadowOffset: { width: 0, height: 2 },
    elevation: 3,
  },
  panelRow: {
    fontSize: 11,
  },
});
